/**
 * Loading data without a query library.
 *
 * Three states a screen actually needs - loading, failed, loaded - plus a
 * reload, and a guard so a response that arrives after the component has moved
 * on cannot overwrite what is on screen.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface Async<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  set: (value: T) => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = []): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true);
    loadRef
      .current()
      .then((result) => {
        if (generation.current !== mine) return;
        setData(result);
        setError(null);
      })
      .catch((problem) => {
        if (generation.current !== mine) return;
        setError(problem);
      })
      .finally(() => {
        if (generation.current === mine) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { data, error, loading, reload, set: setData };
}
